import React from 'react';

type ChangesOverviewTableSkeletonProps = {
    rows?: number;
};

function SkeletonBar({ className = '' }: { className?: string }) {
    return (
        <div className={`animate-pulse rounded bg-gray-200 dark:bg-gray-700 ${className}`} />
    );
}

/** Table-shaped loading placeholder matching ChangesOverviewTable. */
function ChangesOverviewTableSkeleton(props: ChangesOverviewTableSkeletonProps) {
    const rowCount = Math.max(1, props.rows ?? 8);

    return (
        <div
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
            data-testid="changes-overview-table-skeleton"
            aria-busy="true"
            aria-live="polite"
        >
            <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 bg-slate-50 text-left dark:border-gray-700 dark:bg-gray-800">
                            <th className="px-3 py-2.5 w-8">
                                <SkeletonBar className="h-3.5 w-3.5" />
                            </th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-14" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-28" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-16" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-20" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-16" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-24" /></th>
                            <th className="px-3 py-2.5"><SkeletonBar className="h-3 w-16" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: rowCount }).map((_, index) => {
                            const zebra = index % 2 === 1
                                ? 'bg-slate-50/80 dark:bg-gray-800/80'
                                : 'bg-white dark:bg-gray-800';
                            return (
                                <tr key={index} className={`border-b border-gray-100 dark:border-gray-700 ${zebra}`}>
                                    <td className="px-3 py-2.5">
                                        <SkeletonBar className="h-3.5 w-3.5" />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <SkeletonBar className="h-5 w-14" />
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="space-y-1.5">
                                            <SkeletonBar className="h-3.5 w-48 max-w-full" />
                                            <SkeletonBar className="h-2.5 w-32 max-w-full" />
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5"><SkeletonBar className="h-5 w-20" /></td>
                                    <td className="px-3 py-2.5"><SkeletonBar className="h-3 w-24" /></td>
                                    <td className="px-3 py-2.5"><SkeletonBar className="h-6 w-6 rounded-full" /></td>
                                    <td className="px-3 py-2.5"><SkeletonBar className="h-3 w-28" /></td>
                                    <td className="px-3 py-2.5"><SkeletonBar className="h-3 w-16" /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ChangesOverviewTableSkeleton;
